/**
 * `/admin/people/new` — admin "Add person" form.
 *
 * Slim form: the admin captures only the volunteer's first / middle (optional)
 * / last name + email. The volunteer fills in everything else (DOB, contact
 * details, address, attestation declarations) themselves on first sign-in.
 *
 * Defaults applied client-side at create time (everything that NQA ITS
 * requires but the school never customises for v1):
 *   - person_type            = 'volunteer'
 *   - initial_status         = 'onboarding'
 *   - nqa_position           = 'Volunteer'
 *   - nature_of_employment   = 'Direct'
 *   - no_qualification_training = true
 *   - on_probationary_period = null
 *   - wwcc_exemption         = false
 *   - mobile_phone / start_date / DOB / address / preferred_name = null
 *
 * Migration 0034 dropped the stored `full_name` column — the create
 * payload sends the name parts directly and the response DTO projects
 * `full_name` from `concat_ws(...)` at SELECT time.
 *
 * Migration 0033 retired the admin role tier — every admin can submit
 * this form. The server still enforces auth on the route.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../../api/client.js';
import type { ApiClient } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import type { CreatePersonRequest } from '@isms/shared';

export interface PersonNewPageProps {
  client: ApiClient;
  signInPath?: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; error: ApiError | string }
  | { kind: 'ready' };

interface FormState {
  first_name: string;
  middle_name: string;
  last_name: string;
  contact_email: string;
}

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'error'; message: string; apiError: ApiError | null };

function emptyFormState(): FormState {
  return { first_name: '', middle_name: '', last_name: '', contact_email: '' };
}

function buildRequest(form: FormState): CreatePersonRequest {
  const firstName = form.first_name.trim();
  const middleName = form.middle_name.trim();
  const lastName = form.last_name.trim();
  const email = form.contact_email.trim();

  const base: CreatePersonRequest = {
    first_name: firstName,
    middle_name: middleName.length > 0 ? middleName : null,
    last_name: lastName,
    preferred_name: null,
    date_of_birth: null,
    contact_email: email,
    person_type: 'volunteer',
    role_tags: [],
    direct_child_contact: false,
    start_date: null,
    end_date: null,
    initial_status: 'onboarding',
    notes: null,
    nqa_position: 'Volunteer',
    nature_of_employment: 'Direct',
    on_probationary_period: null,
    wwcc_exemption: false,
    no_qualification_training: true,
  };
  return base;
}

function createErrorCopy(err: ApiError): string {
  switch (err.code) {
    case 'email_in_use':
      return 'That email is already on file for another person. Please use a different address.';
    case 'validation_failed':
      return 'Please check the fields above, then try again.';
    default:
      return err.message;
  }
}

export function PersonNewPage({
  client,
  signInPath = '/admin/sign-in',
}: PersonNewPageProps): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [form, setForm] = useState<FormState>(emptyFormState);
  const [submit, setSubmit] = useState<SubmitState>({ kind: 'idle' });
  const navigate = useNavigate();

  const redirectToSignIn = useCallback(() => {
    navigate(signInPath, { replace: true });
  }, [navigate, signInPath]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        await client.getAdminMe();
        if (cancelled) return;
        setState({ kind: 'ready' });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          redirectToSignIn();
          return;
        }
        setState({
          kind: 'error',
          error:
            err instanceof ApiError
              ? err
              : "We couldn't load your profile. Please check your connection and try again.",
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, redirectToSignIn]);

  const onSubmit = useCallback(
    async (ev: FormEvent<HTMLFormElement>) => {
      ev.preventDefault();
      const firstName = form.first_name.trim();
      const lastName = form.last_name.trim();
      const email = form.contact_email.trim();
      if (firstName.length === 0 || lastName.length === 0 || email.length === 0) {
        setSubmit({
          kind: 'error',
          message: 'First name, last name, and email are all required.',
          apiError: null,
        });
        return;
      }

      setSubmit({ kind: 'submitting' });
      try {
        const result = await client.adminCreatePerson(buildRequest(form));
        const id = String(result.person.person_id);
        navigate(`/admin/people/${encodeURIComponent(id)}`, { replace: true });
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          redirectToSignIn();
          return;
        }
        if (err instanceof ApiError) {
          setSubmit({ kind: 'error', message: createErrorCopy(err), apiError: err });
        } else {
          setSubmit({
            kind: 'error',
            message:
              "We couldn't save the new person. Check your connection and try again — no changes were saved.",
            apiError: null,
          });
        }
      }
    },
    [client, form, navigate, redirectToSignIn],
  );

  const isBusy = submit.kind === 'submitting';

  return (
    <main className="admin-page admin-person-new" aria-labelledby="admin-person-new-heading">
      <nav className="admin-people-breadcrumb" aria-label="Breadcrumb">
        <Link to="/admin/people">← Back to people</Link>
      </nav>

      <header className="admin-users-header">
        <h1 id="admin-person-new-heading">Add person</h1>
        <p className="admin-users-intro">
          Capture the volunteer's name and email. They'll get an invite to sign in and complete
          their own profile and compliance tasks.
        </p>
      </header>

      {state.kind === 'loading' ? (
        <p className="admin-people-loading" aria-busy="true">
          Loading…
        </p>
      ) : null}

      {state.kind === 'error' ? <ErrorBanner error={state.error} /> : null}

      {state.kind === 'ready' ? (
        <section className="admin-users-create" aria-labelledby="admin-person-new-form-heading">
          <header className="admin-users-create-header">
            <h2 id="admin-person-new-form-heading">Send an intake invite</h2>
          </header>
          <form className="admin-users-create-form" onSubmit={onSubmit} noValidate>
            <fieldset className="admin-person-form-fieldset">
              <legend>Name</legend>

              <div className="admin-users-form-row">
                <label htmlFor="admin-person-new-first-name">
                  First name <span aria-hidden="true">*</span>
                  <span className="sr-only"> (required)</span>
                </label>
                <input
                  id="admin-person-new-first-name"
                  type="text"
                  autoComplete="off"
                  required
                  value={form.first_name}
                  onChange={(ev) => setForm((f) => ({ ...f, first_name: ev.target.value }))}
                  disabled={isBusy}
                />
              </div>

              <div className="admin-users-form-row">
                <label htmlFor="admin-person-new-middle-name">Middle name</label>
                <input
                  id="admin-person-new-middle-name"
                  type="text"
                  autoComplete="off"
                  value={form.middle_name}
                  onChange={(ev) => setForm((f) => ({ ...f, middle_name: ev.target.value }))}
                  disabled={isBusy}
                />
              </div>

              <div className="admin-users-form-row">
                <label htmlFor="admin-person-new-last-name">
                  Last name <span aria-hidden="true">*</span>
                  <span className="sr-only"> (required)</span>
                </label>
                <input
                  id="admin-person-new-last-name"
                  type="text"
                  autoComplete="off"
                  required
                  value={form.last_name}
                  onChange={(ev) => setForm((f) => ({ ...f, last_name: ev.target.value }))}
                  disabled={isBusy}
                />
              </div>
            </fieldset>

            <fieldset className="admin-person-form-fieldset">
              <legend>Contact</legend>

              <div className="admin-users-form-row">
                <label htmlFor="admin-person-new-email">
                  Email <span aria-hidden="true">*</span>
                  <span className="sr-only"> (required)</span>
                </label>
                <input
                  id="admin-person-new-email"
                  type="email"
                  autoComplete="off"
                  required
                  value={form.contact_email}
                  onChange={(ev) => setForm((f) => ({ ...f, contact_email: ev.target.value }))}
                  disabled={isBusy}
                />
                <p className="admin-users-form-hint">
                  The address the volunteer uses to sign in. Must be unique across the register. The
                  intake invite is sent here.
                </p>
              </div>
            </fieldset>

            <div className="admin-users-create-actions">
              <button type="submit" className="admin-exports-primary-button" disabled={isBusy}>
                {isBusy ? 'Sending…' : 'Send intake invite'}
              </button>
              <Link to="/admin/people" className="admin-exports-secondary-button">
                Cancel
              </Link>
            </div>

            {submit.kind === 'error' ? (
              submit.apiError ? (
                <ErrorBanner error={submit.apiError} message={submit.message} />
              ) : (
                <p className="admin-users-create-error" role="alert">
                  {submit.message}
                </p>
              )
            ) : null}
          </form>
        </section>
      ) : null}
    </main>
  );
}
